/**
 * intakeDDModel.ts
 *
 * Pure logic for the DD Intake Mode: turning a loan tape (XLSX/CSV rows) or a
 * hand-typed list into one loan list, mapping tape columns to loan fields, and
 * counting files for the extract action.
 *
 * No React, no network. Everything is plain data in, plain data out.
 *
 * Requirements: 4.1, 4.2, 4.3, 4.4, 4.5.
 */

import { docTypeMatchesCheck, humanizeCheckName } from '../analyze/analyzeModel';

/** The loan fields a tape column can map to. */
export type LoanField = 'loanNumber' | 'borrowerName' | 'propertyAddress' | 'loanAmount';

export interface LoanFieldInfo {
  id: LoanField;
  label: string;
  /** A loan list cannot be built without this field. */
  required: boolean;
}

export const LOAN_FIELDS: ReadonlyArray<LoanFieldInfo> = [
  { id: 'loanNumber', label: 'Loan number', required: true },
  { id: 'borrowerName', label: 'Borrower name', required: false },
  { id: 'propertyAddress', label: 'Property address', required: false },
  { id: 'loanAmount', label: 'Loan amount', required: false },
];

/** A loan before it exists on the server. Tape and manual entry both produce this. */
export interface DraftLoan {
  /** Position in the tape (or in the typed list). Stable key for later updates. */
  rowIndex: number;
  loanNumber: string;
  borrowerName: string;
  propertyAddress: string;
  loanAmount: string;
}

export interface Tape {
  fileName: string;
  /** Header cells; blank headers are named "Column n". */
  columns: string[];
  /** Data rows, each the same length as `columns`. */
  rows: string[][];
}

/** Field → index of the tape column that feeds it (or null when unmapped). */
export type ColumnMapping = Readonly<Record<LoanField, number | null>>;

export const EMPTY_MAPPING: ColumnMapping = {
  loanNumber: null,
  borrowerName: null,
  propertyAddress: null,
  loanAmount: null,
};

export interface DerivedLoans {
  loans: DraftLoan[];
  /** Rows skipped because a loan with the same loan number came first. */
  duplicates: number;
  /** Rows skipped because the loan number cell was empty. */
  blank: number;
}

// ── Tape parsing ────────────────────────────────────────────────────────────

/** Text of one spreadsheet cell. */
export function cellText(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? '' : value.toISOString().slice(0, 10);
  return String(value).trim();
}

/**
 * Build a Tape from a sheet's rows. The first row with any content is the
 * header. Fully blank data rows are dropped. Returns null when there is no
 * header or no data row.
 */
export function buildTape(fileName: string, matrix: ReadonlyArray<ReadonlyArray<unknown>>): Tape | null {
  const headerAt = matrix.findIndex((row) => row.some((cell) => cellText(cell) !== ''));
  if (headerAt < 0) return null;

  const header = matrix[headerAt];
  const width = Math.max(header.length, ...matrix.slice(headerAt + 1).map((r) => r.length));
  const columns = Array.from({ length: width }, (_, i) => cellText(header[i]) || `Column ${i + 1}`);

  const rows = matrix
    .slice(headerAt + 1)
    .map((row) => Array.from({ length: width }, (_, i) => cellText(row[i])))
    .filter((row) => row.some((cell) => cell !== ''));

  if (rows.length === 0) return null;
  return { fileName, columns, rows };
}

// ── Column mapping ──────────────────────────────────────────────────────────

const HEADER_SYNONYMS: Readonly<Record<LoanField, readonly string[]>> = {
  loanNumber: ['loannumber', 'loanno', 'loannum', 'loanid', 'loanidentifier', 'loan', 'accountnumber', 'loanref'],
  borrowerName: ['borrowername', 'borrower', 'primaryborrower', 'borrowers', 'mortgagor', 'name'],
  propertyAddress: ['propertyaddress', 'address', 'subjectproperty', 'propertyaddr', 'situsaddress', 'property'],
  loanAmount: [
    'loanamount',
    'originalloanamount',
    'originalbalance',
    'currentbalance',
    'principalbalance',
    'upb',
    'balance',
    'amount',
  ],
};

function normalizeHeader(header: string): string {
  return header.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/** Best-guess mapping from header names. A column feeds at most one field. */
export function guessMapping(columns: ReadonlyArray<string>): ColumnMapping {
  const normalized = columns.map(normalizeHeader);
  const used = new Set<number>();
  const result: Record<LoanField, number | null> = { ...EMPTY_MAPPING };
  for (const { id } of LOAN_FIELDS) {
    // Earlier synonyms are stronger matches than later ones.
    for (const synonym of HEADER_SYNONYMS[id]) {
      const at = normalized.findIndex((h, i) => h === synonym && !used.has(i));
      if (at >= 0) {
        result[id] = at;
        used.add(at);
        break;
      }
    }
  }
  return result;
}

/** The field a column feeds, or null when the column is ignored. */
export function fieldForColumn(mapping: ColumnMapping, columnIndex: number): LoanField | null {
  const hit = LOAN_FIELDS.find(({ id }) => mapping[id] === columnIndex);
  return hit ? hit.id : null;
}

/**
 * Point `columnIndex` at `field` (or at nothing when `field` is null). The
 * column's old field is cleared, and so is any other column that fed `field`.
 */
export function setColumnField(
  mapping: ColumnMapping,
  columnIndex: number,
  field: LoanField | null
): ColumnMapping {
  const next: Record<LoanField, number | null> = { ...mapping };
  for (const { id } of LOAN_FIELDS) {
    if (next[id] === columnIndex) next[id] = null;
  }
  if (field) next[field] = columnIndex;
  return next;
}

export interface MappingCounts {
  mapped: number;
  ignored: number;
  total: number;
}

export function mappingCounts(mapping: ColumnMapping, columnCount: number): MappingCounts {
  const mappedColumns = new Set(
    LOAN_FIELDS.map(({ id }) => mapping[id]).filter((i): i is number => i !== null && i < columnCount)
  );
  return { mapped: mappedColumns.size, ignored: columnCount - mappedColumns.size, total: columnCount };
}

/** "4 of 33 columns mapped · 29 ignored" */
export function formatMappingCounts(counts: MappingCounts): string {
  return `${counts.mapped} of ${counts.total} ${counts.total === 1 ? 'column' : 'columns'} mapped · ${counts.ignored} ignored`;
}

// ── Loan list ───────────────────────────────────────────────────────────────

/**
 * The loan list a tape gives under a mapping. Needs the loan number column:
 * without it there is no list. Blank and repeated loan numbers are skipped and
 * counted.
 */
export function deriveLoans(tape: Tape, mapping: ColumnMapping): DerivedLoans {
  const result: DerivedLoans = { loans: [], duplicates: 0, blank: 0 };
  if (mapping.loanNumber === null) return result;

  const seen = new Set<string>();
  const pick = (row: string[], field: LoanField) => {
    const at = mapping[field];
    return at === null ? '' : (row[at] ?? '');
  };

  tape.rows.forEach((row, rowIndex) => {
    const loanNumber = pick(row, 'loanNumber');
    if (!loanNumber) {
      result.blank += 1;
      return;
    }
    const key = loanNumber.toLowerCase();
    if (seen.has(key)) {
      result.duplicates += 1;
      return;
    }
    seen.add(key);
    result.loans.push({
      rowIndex,
      loanNumber,
      borrowerName: pick(row, 'borrowerName'),
      propertyAddress: pick(row, 'propertyAddress'),
      loanAmount: pick(row, 'loanAmount'),
    });
  });
  return result;
}

/**
 * Manual entry: one loan per line, "loan number" or "loan number, borrower name".
 * Gives the same DerivedLoans a tape does, so both feed one loan list.
 */
export function parseManualLoans(text: string): DerivedLoans {
  const result: DerivedLoans = { loans: [], duplicates: 0, blank: 0 };
  const seen = new Set<string>();
  text.split(/\r?\n/).forEach((line, rowIndex) => {
    if (line.trim() === '') return;
    const comma = line.indexOf(',');
    const loanNumber = (comma < 0 ? line : line.slice(0, comma)).trim();
    const borrowerName = comma < 0 ? '' : line.slice(comma + 1).trim();
    if (!loanNumber) {
      result.blank += 1;
      return;
    }
    const key = loanNumber.toLowerCase();
    if (seen.has(key)) {
      result.duplicates += 1;
      return;
    }
    seen.add(key);
    result.loans.push({ rowIndex, loanNumber, borrowerName, propertyAddress: '', loanAmount: '' });
  });
  return result;
}

// ── Documents and the extract action ────────────────────────────────────────

const SUPPORTED_EXTENSIONS = new Set(['pdf', 'png', 'jpg', 'jpeg', 'tiff', 'tif', 'bmp', 'gif']);
export const SUPPORTED_FILE_TYPES_DISPLAY = 'PDF, PNG, JPG, JPEG, TIFF, BMP, GIF';
export const ACCEPTED_FILE_TYPES = '.pdf,.png,.jpg,.jpeg,.tiff,.tif,.bmp,.gif';

export function isSupportedDocument(filename: string): boolean {
  const dot = filename.lastIndexOf('.');
  if (dot < 0) return false;
  return SUPPORTED_EXTENSIONS.has(filename.slice(dot + 1).toLowerCase());
}

/** Files already on the loan plus files uploaded and waiting for extraction. */
export function loanFileTotal(storedFileCount: number, pendingCount: number): number {
  return storedFileCount + pendingCount;
}

/** Files the extract action will send: everything uploaded and not yet extracted. */
export function extractFileCount(pending: Readonly<Record<string, ReadonlyArray<string>>>): number {
  return Object.values(pending).reduce((sum, names) => sum + names.length, 0);
}

export function formatExtractLabel(count: number): string {
  return `Extract ${count} ${count === 1 ? 'file' : 'files'}`;
}

/**
 * The loan a file belongs to when it comes from a folder named by loan number:
 * "LN-1001/note.pdf" → the loan with number "LN-1001". Matching ignores case.
 */
export function matchFolderToLoan<T extends { loanNumber?: string | null }>(
  relativePath: string,
  loans: ReadonlyArray<T>
): T | undefined {
  const parts = relativePath.split(/[\\/]/).filter(Boolean);
  if (parts.length < 2) return undefined;
  // Files may sit in sub-folders, so try each folder name from the outside in.
  for (const folder of parts.slice(0, -1)) {
    const key = folder.trim().toLowerCase();
    const hit = loans.find((l) => (l.loanNumber ?? '').trim().toLowerCase() === key);
    if (hit) return hit;
  }
  return undefined;
}

// ── Document completeness per loan ──────────────────────────────────────────

/** How many of a guideline's required document types a loan has. */
export interface DocCompleteness {
  have: number;
  total: number;
  /** Required types with no matching document, in guideline order. */
  missing: string[];
  complete: boolean;
}

/** "Final_HUD-1.pdf" → "Final_HUD-1". */
export function fileNameStem(fileName: string): string {
  const dot = fileName.lastIndexOf('.');
  return dot > 0 ? fileName.slice(0, dot) : fileName;
}

/**
 * Required document types the loan has, from the names of its documents
 * (extracted document types, and the names of files uploaded and waiting for
 * extraction). Matching is the one Analyze uses for the completeness check, so
 * the two screens agree. Returns null when no required set is known: the
 * caller then shows the file count alone.
 */
export function loanDocCompleteness(
  required: ReadonlyArray<string>,
  presentNames: ReadonlyArray<string>
): DocCompleteness | null {
  const types = Array.from(new Set(required.map((r) => r.trim()).filter(Boolean)));
  if (types.length === 0) return null;
  const missing = types.filter((type) => !presentNames.some((name) => docTypeMatchesCheck(name, type)));
  const have = types.length - missing.length;
  return {
    have,
    total: types.length,
    missing: missing.map(humanizeCheckName),
    complete: missing.length === 0,
  };
}

/** "3 of 4 docs" */
export function formatDocCount(completeness: Pick<DocCompleteness, 'have' | 'total'>): string {
  return `${completeness.have} of ${completeness.total} ${completeness.total === 1 ? 'doc' : 'docs'}`;
}

/**
 * Ids of the loans that lack a required document, in loan order. Empty when no
 * required set is known. This is what a Rail count note ("n need you") selects.
 */
export function incompleteLoanIds(
  loans: ReadonlyArray<{ loanId: string }>,
  required: ReadonlyArray<string>,
  presentNamesByLoan: Readonly<Record<string, ReadonlyArray<string>>>
): string[] {
  return loans
    .filter((loan) => {
      const result = loanDocCompleteness(required, presentNamesByLoan[loan.loanId] ?? []);
      return result !== null && !result.complete;
    })
    .map((loan) => loan.loanId);
}

/** URL param that limits the Intake table to loans with missing documents. */
export const DOCS_FILTER_PARAM = 'docs';
export const DOCS_FILTER_INCOMPLETE = 'incomplete';

export function isIncompleteFilterOn(search: URLSearchParams | string | null | undefined): boolean {
  return new URLSearchParams(search ?? '').get(DOCS_FILTER_PARAM) === DOCS_FILTER_INCOMPLETE;
}

/** Copy of `search` with the incomplete-documents filter switched on or off. */
export function withIncompleteFilter(
  search: URLSearchParams | string | null | undefined,
  on: boolean
): URLSearchParams {
  const params = new URLSearchParams(search ?? '');
  if (on) params.set(DOCS_FILTER_PARAM, DOCS_FILTER_INCOMPLETE);
  else params.delete(DOCS_FILTER_PARAM);
  return params;
}

/** Splits a job's `s3://bucket/prefix/` upload location. */
export function parseUploadLocation(location: string | undefined | null): { bucket: string; prefix: string } | null {
  const match = /^s3:\/\/([^/]+)\/?(.*)$/.exec(location ?? '');
  return match ? { bucket: match[1], prefix: match[2] } : null;
}
